import { Flow } from '@thive/fddm-types';

import { InvalidFlow } from './types';

const validateConsolidationGroups = (flows: Flow[]): InvalidFlow[] => {
  const groupedByArea = flows.reduce((map, flow) => {
    const key =
      flow.type === 'CONSOLIDATION_STORAGE'
        ? flow.destinationAreaOrFlowStopId
        : flow.sourceAreaOrFlowStopId;
    const existing = map.get(key) ?? [];
    map.set(key, [...existing, flow]);
    return map;
  }, new Map<string, Flow[]>());

  const invalid: InvalidFlow[] = [];

  groupedByArea.forEach((areaFlows) => {
    const storageFlows = areaFlows.filter((f) => f.type === 'CONSOLIDATION_STORAGE');
    const retrievalFlows = areaFlows.filter((f) => f.type === 'CONSOLIDATION_RETRIEVAL');

    const totalStorage = storageFlows.reduce(
      (sum, f) => sum + (f.consolidationStorageExtension?.numConsolidations ?? 0),
      0,
    );

    retrievalFlows.forEach((retrieval) => {
      const retrievalAmount = retrieval.consolidationRetrievalExtension?.numConsolidations ?? 0;
      const flow = storageFlows[0];

      if (retrievalAmount > totalStorage && storageFlows.length > 0) {
        invalid.push({
          areaId: flow.destinationAreaOrFlowStopId || '?',
          areaName: flow.destinationAreaOrFlowStopId || '?',
          reason: 'more_consolidations_out_then_in',
          flowId: retrieval.id,
          context: { amount: totalStorage },
        });
      }
    });
  });

  return invalid;
};

const validateBatchSize = (flows: Flow[]): InvalidFlow[] =>
  flows
    .filter((f) => f.type === 'CONSOLIDATION_STORAGE')
    .flatMap((flow) => {
      const invalids: InvalidFlow[] = [];

      const { consolidationStorageExtension, hourlyOrderCounts, destinationAreaOrFlowStopId } =
        flow;
      if (!consolidationStorageExtension) return invalids;

      const { numConsolidations, batchSize } = consolidationStorageExtension;
      const totalNumLoads = hourlyOrderCounts[0];

      if (numConsolidations * batchSize > totalNumLoads) {
        invalids.push({
          areaId: destinationAreaOrFlowStopId || '?',
          areaName: destinationAreaOrFlowStopId || '?',
          reason: 'batch_size_too_large',
          flowId: flow.id,
        });
      }

      if (totalNumLoads % (batchSize * numConsolidations) !== 0) {
        invalids.push({
          areaId: destinationAreaOrFlowStopId || '?',
          areaName: destinationAreaOrFlowStopId || '?',
          reason: 'batch_size_mismatch_loads_per_hour',
          flowId: flow.id,
        });
      }

      return invalids;
    });

const validateShipmentSize = (flows: Flow[]): InvalidFlow[] =>
  flows
    .filter((f) => f.type === 'CONSOLIDATION_RETRIEVAL')
    .flatMap((flow) => {
      const invalidFlows: InvalidFlow[] = [];

      const { consolidationRetrievalExtension, hourlyOrderCounts, sourceAreaOrFlowStopId } = flow;
      if (!consolidationRetrievalExtension) return invalidFlows;

      const { numConsolidations, shipmentSize } = consolidationRetrievalExtension;
      const totalNumLoads = hourlyOrderCounts[0];

      if (shipmentSize >= totalNumLoads) {
        invalidFlows.push({
          areaId: sourceAreaOrFlowStopId || '?',
          areaName: sourceAreaOrFlowStopId || '?',
          reason: 'shipment_size_too_large',
          flowId: flow.id,
        });
      }

      if (totalNumLoads % shipmentSize !== 0) {
        invalidFlows.push({
          areaId: sourceAreaOrFlowStopId || '?',
          areaName: sourceAreaOrFlowStopId || '?',
          reason: 'shipment_size_mismatch_loads_per_hour',
          flowId: flow.id,
        });
      }

      if (shipmentSize % numConsolidations !== 0) {
        invalidFlows.push({
          areaId: sourceAreaOrFlowStopId || '?',
          areaName: sourceAreaOrFlowStopId || '?',
          reason: 'shipment_size_mismatch_consolidations',
          flowId: flow.id,
        });
      }

      return invalidFlows;
    });

export const validateConsolidationFlows = (flows: Flow[]): InvalidFlow[] => [
  ...validateConsolidationGroups(flows),
  ...validateBatchSize(flows),
  ...validateShipmentSize(flows),
];
